import type { WorkspaceRow } from "@elaso/shared/types/db";
import {
	LTI_PLATFORM_START_REFUSALS,
	type WorkspaceElsewhereStartResult,
} from "@elaso/shared/types/lti";
import { toast } from "sonner";
import { ulid } from "ulid";
import { z } from "zod";
import { createStore } from "zustand";
import { requestAuthToken, SessionRefusedError } from "@/lib/api";
import {
	homeConnectionTarget,
	setConnectionTarget,
} from "@/lib/connection-target";
import {
	firebaseSession,
	homeSession,
	type InstanceSignIn,
	signInAtInstance,
} from "@/lib/firebase";
import { reloadPage } from "@/lib/reload-page";
import { sentryLogger } from "@/lib/sentry";
import { resetAccountState } from "@/lib/stores/account-state";
import { ws } from "@/lib/websocket-wrapper";
import {
	type AccessEnded,
	type AccessEndedNames,
	accessEndedFrom,
	accessEndedWords,
} from "./access-ended-copy";
import { selectLaunchedWorkspace } from "./launch-flow";
import { START_REFUSED } from "./platform-launch";

export type SwitchOutcome =
	| { ok: true; workspaceId: string | null }
	| { ok: false; ended: AccessEnded | null; message: string };

type Failure = Extract<SwitchOutcome, { ok: false }>;

type SwitchEntry = { connectionId: string; workspaceName?: string };

const LoginCallSchema = z.object({ authRequestUrl: z.url() });

const AuthCallSchema = z.object({
	launchUrl: z.url(),
	fields: z.record(z.string(), z.string()),
});

const LaunchCallSchema = z.object({
	customToken: z.string().min(1),
	workspaceId: z.string().min(1),
	gcipTenantId: z.string().min(1),
	instance: z.object({
		serverUrl: z.url(),
		wsUrl: z.url(),
		livekitUrl: z.url(),
		firebase: z.object({
			apiKey: z.string().min(1),
			authDomain: z.string().min(1),
			projectId: z.string().min(1),
			appId: z.string().min(1),
		}),
	}),
	sessionMaxAgeMs: z.number().positive().optional(),
});

type LaunchCall = z.infer<typeof LaunchCallSchema>;

const StartCallSchema: z.ZodType<WorkspaceElsewhereStartResult> = z.union([
	z.object({
		ok: z.literal(true),
		initiationUrl: z.url(),
		carry: z.enum(["page", "calls"]),
	}),
	z.object({
		ok: z.literal(false),
		reason: z.enum([...LTI_PLATFORM_START_REFUSALS, "consent_required"]),
	}),
]);

const RENEW_AT_FRACTION_OF_MAX_AGE = 0.75;

const CALL_TIMEOUT_MS = 10_000;

const STILL_HERE = "You're still signed in here.";

const SIGN_IN_REFUSED = `The other ELASO Instance didn't accept the sign-in. ${STILL_HERE}`;
const NOT_AN_INSTANCE = `This LTI Tool is no longer registered as another ELASO Instance, so nothing was switched. ${STILL_HERE}`;
const NOT_AWAY = "This client is already on your own ELASO Instance.";
const SWITCH_UNDER_WAY =
	"A switch between ELASO Instances is already under way. Try again once it finishes.";
const HOME_SIGNED_OUT = `Your sign-in on your own ELASO Instance has ended. Reload to sign in there again. ${STILL_HERE}`;
const HOME_NOT_RENEWED = `Your sign-in on your own ELASO Instance couldn't be renewed. ${STILL_HERE}`;
const HOME_REFUSED = `Your own ELASO Instance refused your session. ${STILL_HERE}`;
const NOTHING_TO_RENEW =
	"This session was not opened from a Shared Workspace entry, so it cannot be renewed.";
const RENEWAL_REFUSED =
	"The other ELASO Instance refused to renew this session.";

type Away = {
	homeWorkspaces: WorkspaceRow[];
	homeOrganization: string | null;
	signIn: InstanceSignIn;
	connectionId: string | null;
	workspaceName: string | null;
	endedBy?: AccessEnded | null;
};

export const awayStore = createStore<{ away: Away | null }>(() => ({
	away: null,
}));

const snapshotHomeWorkspaces = async (): Promise<WorkspaceRow[]> => {
	const { coreStore } = await import("@/lib/stores/core-store");
	if (coreStore.getState().workspaces.size === 0) {
		try {
			await coreStore.getState().fetchWorkspaces();
		} catch {
			return [];
		}
	}
	return Array.from(coreStore.getState().workspaces.values());
};

const homeOrganizationHere = async (): Promise<string | null> => {
	const uid = firebaseSession.getCurrentUserId();
	if (!uid) return null;
	const { coreStore } = await import("@/lib/stores/core-store");
	return (
		coreStore.getState().users.get(uid)?.profile.organization?.name ?? null
	);
};

const homeOrganizationName = (): Promise<string | null> => {
	const away = awayStore.getState().away;
	return away ? Promise.resolve(away.homeOrganization) : homeOrganizationHere();
};

const namesOf = (away: Away): AccessEndedNames => ({
	workspace: away.workspaceName,
	homeOrganization: away.homeOrganization,
});

type Halt = { ended: AccessEnded | null; other?: string };

const other = (message: string): Halt => ({ ended: null, other: message });

const failure = (halt: Halt, names: AccessEndedNames): Failure => ({
	ok: false,
	ended: halt.ended,
	message: halt.other ?? `${accessEndedWords(halt.ended, names)} ${STILL_HERE}`,
});

class StartHalted extends Error {
	readonly halt: Halt;

	constructor(halt: Halt) {
		super("The launch was not started");
		this.halt = halt;
	}
}

type CallResult<T> =
	| { ok: true; body: T }
	| { ok: false; reason: "unreachable" }
	| { ok: false; reason: "refused"; code: string };

const readCode = (body: unknown): string => {
	const code = (body as { code?: unknown } | null)?.code;
	return typeof code === "string" ? code : "internal";
};

const call = async <T>(
	url: string,
	schema: z.ZodType<T>,
	init: RequestInit = {}
): Promise<CallResult<T>> => {
	const signal = AbortSignal.timeout(CALL_TIMEOUT_MS);
	let response: Response;
	try {
		response = await fetch(url, {
			...init,
			headers: { accept: "application/json", ...init.headers },
			credentials: "omit",
			signal,
		});
	} catch {
		return { ok: false, reason: "unreachable" };
	}
	let body: unknown = null;
	try {
		body = await response.json();
	} catch {
		if (signal.aborted) return { ok: false, reason: "unreachable" };
	}
	if (!response.ok)
		return { ok: false, reason: "refused", code: readCode(body) };
	const parsed = schema.safeParse(body);
	return parsed.success
		? { ok: true, body: parsed.data }
		: { ok: false, reason: "refused", code: "internal" };
};

const failedCall = (
	result: Extract<CallResult<unknown>, { ok: false }>,
	unreachable: AccessEnded
): Halt => ({
	ended:
		result.reason === "unreachable"
			? unreachable
			: accessEndedFrom(result.code),
});

const discardQuietly = (signIn: InstanceSignIn): Promise<void> =>
	signIn.discard().catch((error: unknown) => {
		sentryLogger.warn(
			"An unused sign-in at another ELASO Instance was not discarded",
			{
				error: String(error),
			}
		);
	});

type Carried =
	| {
			ok: true;
			signIn: InstanceSignIn;
			sessionToken: string;
			launch: LaunchCall;
	  }
	| { ok: false; halt: Halt };

const carry = async (
	start: () => Promise<WorkspaceElsewhereStartResult>
): Promise<Carried> => {
	let started: WorkspaceElsewhereStartResult;
	try {
		started = await start();
	} catch (error) {
		if (error instanceof StartHalted) return { ok: false, halt: error.halt };
		return {
			ok: false,
			halt: other(error instanceof Error ? error.message : String(error)),
		};
	}
	if (!started.ok) {
		const words = START_REFUSED[started.reason];
		return {
			ok: false,
			halt: words ? other(words) : { ended: accessEndedFrom(started.reason) },
		};
	}
	if (started.carry !== "calls") {
		return { ok: false, halt: other(NOT_AN_INSTANCE) };
	}

	const login = await call(started.initiationUrl, LoginCallSchema);
	if (!login.ok) {
		return { ok: false, halt: failedCall(login, "workspace_unavailable") };
	}
	const auth = await call(login.body.authRequestUrl, AuthCallSchema);
	if (!auth.ok) {
		return { ok: false, halt: failedCall(auth, "institution_unreachable") };
	}
	const launch = await call(auth.body.launchUrl, LaunchCallSchema, {
		method: "POST",
		body: new URLSearchParams(auth.body.fields),
	});
	if (!launch.ok) {
		return { ok: false, halt: failedCall(launch, "workspace_unavailable") };
	}
	const { customToken, gcipTenantId, instance } = launch.body;

	let signIn: InstanceSignIn;
	try {
		signIn = await signInAtInstance({
			firebase: instance.firebase,
			tenantId: gcipTenantId,
			customToken,
		});
	} catch {
		return { ok: false, halt: other(SIGN_IN_REFUSED) };
	}
	try {
		const sessionToken = await requestAuthToken(
			await signIn.getIdToken(),
			instance.serverUrl
		);
		return { ok: true, signIn, sessionToken, launch: launch.body };
	} catch {
		await discardQuietly(signIn);
		return { ok: false, halt: other(SIGN_IN_REFUSED) };
	}
};

let stopWatchingHome: (() => void) | null = null;

const stopWatchingHomeSignIn = (): void => {
	stopWatchingHome?.();
	stopWatchingHome = null;
};

/**
 * Another account has signed in at home, in another tab, with nobody signed
 * out first, as an LTI launch does on a shared machine. The session here is the
 * previous account's, so it ends. Home is NOT signed out, because that sign-in
 * is the new account's now. The tab reloads to take up whoever is signed in:
 * a tab signed in at another identity home's pool still holds the previous
 * account in memory, and a reload is what lets it go.
 */
const leaveForAnotherAccount = async (): Promise<void> => {
	const away = awayStore.getState().away;
	if (!away) return;
	cancelRenewal();
	awayStore.setState({ away: null });
	ws.disconnect();
	try {
		await away.signIn.discard();
	} finally {
		reloadPage();
	}
};

/**
 * While this tab is away, the home sign-in can end or change hands in another
 * tab, and either must reach this one
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md). The session here
 * listens to the away sign-in, which is this tab's own, so it would never hear
 * of it.
 *
 * - A sign-out at home signs the away sign-in out, which sends this tab
 *   through the sign-out every other tab takes.
 * - Another account signing in at home ends the session here without signing
 *   home out (`leaveForAnotherAccount`).
 */
const watchHomeSignIn = (): void => {
	if (stopWatchingHome) return;
	const wentAwayAs = homeSession.uid();
	stopWatchingHome = homeSession.onAccountChanged((uid) => {
		if (uid === wentAwayAs) return;
		if (uid === null) {
			firebaseSession.logout().catch((error: unknown) => {
				sentryLogger.warn(
					"Away session not signed out after a sign-out at home",
					{
						error: String(error),
					}
				);
			});
			return;
		}
		stopWatchingHomeSignIn();
		leaveForAnotherAccount().catch((error: unknown) => {
			sentryLogger.warn(
				"Away session not fully ended after another account signed in at home",
				{
					error: String(error),
				}
			);
		});
	});
};

let renewalTimer: ReturnType<typeof setTimeout> | null = null;

const cancelRenewal = (): void => {
	if (renewalTimer) clearTimeout(renewalTimer);
	renewalTimer = null;
};

const scheduleRenewal = (away: Away, sessionMaxAgeMs?: number): void => {
	cancelRenewal();
	if (!(away.connectionId && sessionMaxAgeMs)) return;
	renewalTimer = setTimeout(() => {
		renewalTimer = null;
		renewAway()
			.then((outcome) => {
				if (!outcome.ok) {
					sentryLogger.warn("Cross-Instance session not renewed", {
						message: outcome.message,
					});
				}
			})
			.catch((error: unknown) => {
				sentryLogger.warn("Cross-Instance renewal threw", {
					error: String(error),
				});
			});
	}, sessionMaxAgeMs * RENEW_AT_FRACTION_OF_MAX_AGE);
};

const run = async (
	start: () => Promise<WorkspaceElsewhereStartResult>,
	entry: SwitchEntry | undefined
): Promise<SwitchOutcome> => {
	const workspaceName = entry?.workspaceName ?? null;
	const carried = await carry(start);
	if (!carried.ok) {
		return failure(carried.halt, {
			workspace: workspaceName,
			homeOrganization: await homeOrganizationName(),
		});
	}
	const { signIn, launch } = carried;
	const { workspaceId, instance } = launch;

	const previous = awayStore.getState().away;
	const homeWorkspaces =
		previous?.homeWorkspaces ?? (await snapshotHomeWorkspaces());
	const homeOrganization = previous
		? previous.homeOrganization
		: await homeOrganizationHere();
	ws.disconnect();
	setConnectionTarget({
		serverUrl: instance.serverUrl,
		wsUrl: instance.wsUrl,
		livekitUrl: instance.livekitUrl,
	});
	await resetAccountState();
	await selectLaunchedWorkspace({ kind: "workspace", workspaceId });
	signIn.adopt();
	const away: Away = {
		homeWorkspaces,
		homeOrganization,
		signIn,
		connectionId: entry?.connectionId ?? null,
		workspaceName,
	};
	awayStore.setState({ away });
	scheduleRenewal(away, launch.sessionMaxAgeMs);
	watchHomeSignIn();
	await previous?.signIn.discard();
	return { ok: true, workspaceId };
};

const homeIdToken = async (): Promise<
	| { ok: true; idToken: string }
	| { ok: false; signedOut: boolean; message: string }
> => {
	try {
		const idToken = await homeSession.getIdToken();
		return idToken
			? { ok: true, idToken }
			: { ok: false, signedOut: true, message: HOME_SIGNED_OUT };
	} catch {
		return { ok: false, signedOut: false, message: HOME_NOT_RENEWED };
	}
};

const startFromHome = async (
	connectionId: string
): Promise<WorkspaceElsewhereStartResult> => {
	const token = await homeIdToken();
	if (!token.ok) throw new StartHalted(other(token.message));
	const started = await call(
		`${homeConnectionTarget.serverUrl}/api/workspaces-elsewhere/start`,
		StartCallSchema,
		{
			method: "POST",
			headers: {
				authorization: `Bearer ${token.idToken}`,
				"content-type": "application/json",
			},
			body: JSON.stringify({ connectionId }),
		}
	);
	if (!started.ok) {
		throw new StartHalted(failedCall(started, "institution_unreachable"));
	}
	return started.body;
};

const notRenewed = (away: Away, halt: Halt): Failure => {
	if (awayStore.getState().away === away) {
		awayStore.setState({ away: { ...away, endedBy: halt.ended } });
	}
	return failure(halt, namesOf(away));
};

const runRenewal = async (): Promise<SwitchOutcome> => {
	const away = awayStore.getState().away;
	if (!away?.connectionId) {
		return { ok: false, ended: null, message: NOTHING_TO_RENEW };
	}
	const { connectionId } = away;

	const carried = await carry(() => startFromHome(connectionId));
	if (!carried.ok) return notRenewed(away, carried.halt);
	const { signIn, sessionToken, launch } = carried;
	try {
		await ws.sendWithPromise({
			id: ulid(),
			procedure: "connection.renew",
			data: { token: sessionToken },
		});
	} catch {
		await discardQuietly(signIn);
		return notRenewed(away, other(RENEWAL_REFUSED));
	}
	if (awayStore.getState().away !== away) {
		await discardQuietly(signIn);
		return { ok: false, ended: null, message: SWITCH_UNDER_WAY };
	}
	signIn.adopt();
	const renewed = { ...away, signIn };
	awayStore.setState({ away: renewed });
	scheduleRenewal(renewed, launch.sessionMaxAgeMs);
	await discardQuietly(away.signIn);
	return { ok: true, workspaceId: launch.workspaceId };
};

const goHome = async (
	away: Away,
	workspaceId: string | null
): Promise<void> => {
	cancelRenewal();
	stopWatchingHomeSignIn();
	ws.disconnect();
	setConnectionTarget(homeConnectionTarget);
	await resetAccountState();
	if (workspaceId) {
		await selectLaunchedWorkspace({ kind: "workspace", workspaceId });
	}
	homeSession.adopt();
	awayStore.setState({ away: null });
	await away.signIn.discard();
};

type HomeAnswer =
	| { kind: "open" }
	| { kind: "closed"; message: string }
	| { kind: "unavailable"; failure: Failure };

const askHome = async (away: Away): Promise<HomeAnswer> => {
	const token = await homeIdToken();
	if (!token.ok) {
		return token.signedOut
			? { kind: "closed", message: token.message }
			: {
					kind: "unavailable",
					failure: { ok: false, ended: null, message: token.message },
				};
	}
	try {
		await requestAuthToken(token.idToken, homeConnectionTarget.serverUrl);
	} catch (error) {
		return error instanceof SessionRefusedError
			? { kind: "closed", message: HOME_REFUSED }
			: {
					kind: "unavailable",
					failure: failure({ ended: "institution_unreachable" }, namesOf(away)),
				};
	}
	return { kind: "open" };
};

const runReturn = async (
	workspaceId: string | null
): Promise<SwitchOutcome> => {
	const away = awayStore.getState().away;
	if (!away) return { ok: false, ended: null, message: NOT_AWAY };

	const answer = await askHome(away);
	switch (answer.kind) {
		case "open":
			await goHome(away, workspaceId);
			return { ok: true, workspaceId };
		case "closed":
			return { ok: false, ended: null, message: answer.message };
		case "unavailable":
			return answer.failure;
		default: {
			const unreachable: never = answer;
			throw new Error(`Unknown home answer: ${JSON.stringify(unreachable)}`);
		}
	}
};

const runEnd = async (
	away: Away,
	workspaceId: string | null
): Promise<SwitchOutcome> => {
	const answer = await askHome(away);
	switch (answer.kind) {
		case "open":
			await goHome(away, workspaceId);
			if (away.endedBy !== undefined) {
				toast.error(
					`${accessEndedWords(away.endedBy, namesOf(away))} ${STILL_HERE}`
				);
			}
			return { ok: true, workspaceId };
		case "closed":
			await endAway();
			return { ok: false, ended: null, message: answer.message };
		case "unavailable":
			if (awayStore.getState().away === away) {
				await goHome(away, workspaceId);
				ws.resumeReconnecting();
				toast.error(answer.failure.message);
			}
			return answer.failure;
		default: {
			const unreachable: never = answer;
			throw new Error(`Unknown home answer: ${JSON.stringify(unreachable)}`);
		}
	}
};

type Request = "switch" | "renew" | `return:${string}`;

let inFlight: { request: Request; outcome: Promise<SwitchOutcome> } | null =
	null;

const once = (
	request: Request,
	perform: () => Promise<SwitchOutcome>
): Promise<SwitchOutcome> => {
	if (inFlight) {
		return inFlight.request === request
			? inFlight.outcome
			: Promise.resolve({ ok: false, ended: null, message: SWITCH_UNDER_WAY });
	}
	const outcome = perform().finally(() => {
		inFlight = null;
	});
	inFlight = { request, outcome };
	return outcome;
};

/**
 * Carry a launch into another ELASO Instance as calls and switch this client
 * there in place: sign in there and take a session token there first, and only
 * then close the connection here and open one there.
 */
export function switchInstance(
	start: () => Promise<WorkspaceElsewhereStartResult>,
	entry?: SwitchEntry
): Promise<SwitchOutcome> {
	return once("switch", () => run(start, entry));
}

export function renewAway(): Promise<SwitchOutcome> {
	return once("renew", runRenewal);
}

export function returnHome(workspaceId: string | null): Promise<SwitchOutcome> {
	return once(`return:${workspaceId ?? ""}`, () => runReturn(workspaceId));
}

const settleInFlight = async (): Promise<void> => {
	while (inFlight) {
		await inFlight.outcome.catch(() => undefined);
	}
};

export const endCrossInstanceSession = async (): Promise<void> => {
	await settleInFlight();
	const away = awayStore.getState().away;
	if (!away) return;
	const firstHome = away.homeWorkspaces[0]?.id ?? null;
	await once(`return:${firstHome ?? ""}`, () => runEnd(away, firstHome));
};

export const endAway = async (): Promise<void> => {
	const away = awayStore.getState().away;
	if (!away) return;
	cancelRenewal();
	stopWatchingHomeSignIn();
	awayStore.setState({ away: null });
	setConnectionTarget(homeConnectionTarget);
	try {
		await homeSession.signOut();
	} finally {
		homeSession.adopt();
		await away.signIn.discard();
	}
};

export const __resetInstanceSwitch = (): void => {
	cancelRenewal();
	stopWatchingHomeSignIn();
	inFlight = null;
	awayStore.setState({ away: null });
};
